import { useMemo, useState } from "react"
import { Table } from "reactstrap"
import ActionButton from "Components/atoms/buttons/action-button"
import SelectInput from "Components/molecule/inputs/select-input"
import CheckboxInput from "Components/molecule/inputs/checkbox-input"
import EmptyState from "Components/organisms/empty-state"
import BillsSection from "./bills-section"
import { ExpenseBudget, ExpenseBudgetLine } from "models/expense-budget"
import { saveExpenseBudgetLineMapping } from "slices/finance/expense-budget/thunk"
import { lineAnnualCad, sumAmounts } from "utils/expense-budget"
import { formatCurrency } from "utils/tools"

interface MappingTabProps {
    budget: ExpenseBudget
    lines: ExpenseBudgetLine[]
    onLinesChanged: () => Promise<void>
}

const UNMAPPED = ""

// Assigns each budget line to a cost code. Changes stay pending until "Save mapping".
const CostCodeMapping = ({ budget, lines, onLinesChanged }: MappingTabProps) => {
    const [pending, setPending] = useState<Record<string, string>>({})
    const [isOnlyUnmapped, setIsOnlyUnmapped] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const costCodeOf = (line: ExpenseBudgetLine) => pending[line.id] ?? line.expenseBudgetCostCodeId ?? UNMAPPED
    const unmappedLines = lines.filter(line => costCodeOf(line) === UNMAPPED)
    const visibleLines = isOnlyUnmapped ? unmappedLines : lines
    const pendingCount = Object.keys(pending).length

    const options = useMemo(() => [
        { value: UNMAPPED, label: "— Unmapped —" },
        ...budget.costCodes.map(c => ({
            value: c.id,
            label: `${c.project ? `${c.project.code.trim()} · ` : ""}${c.transactionAccount.code.trim()} — ${c.transactionAccount.description}`,
        })),
    ], [budget.costCodes])

    const assign = (lineId: string, costCodeId: string) =>
        setPending(prev => ({ ...prev, [lineId]: costCodeId }))

    const handleSave = async () => {
        setIsSaving(true)
        const mappings = Object.entries(pending).map(([expenseBudgetLineId, costCodeId]) => ({
            expenseBudgetLineId,
            expenseBudgetCostCodeId: costCodeId === UNMAPPED ? null : costCodeId,
        }))
        const isSaved = await saveExpenseBudgetLineMapping(budget.id, mappings)
        setIsSaving(false)
        if (!isSaved) return
        setPending({})
        await onLinesChanged()
    }

    if (budget.costCodes.length === 0) return <EmptyState title="No cost codes yet" subtitle="Add cost codes in the Cost Codes tab, then map the lines here." />

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2">
                <div>
                    <h5 className="mb-1">Products by cost code</h5>
                    <span className={unmappedLines.length ? "text-danger" : "text-success"}>
                        {unmappedLines.length} lines unmapped · {formatCurrency(sumAmounts(unmappedLines.map(lineAnnualCad)))}
                    </span>
                </div>
                <div className="d-flex flex-wrap align-items-end gap-2">
                    <CheckboxInput label="Only unmapped" checked={isOnlyUnmapped} onChange={e => setIsOnlyUnmapped(e.target.checked)} />
                    <ActionButton disabled={pendingCount === 0 || isSaving} onClick={handleSave}>
                        {isSaving ? "Saving..." : `Save mapping${pendingCount ? ` (${pendingCount})` : ""}`}
                    </ActionButton>
                </div>
            </div>
            <Table size="sm" hover className="align-middle">
                <thead>
                    <tr><th>Vendor</th><th>Product / License</th><th className="text-end">Annual (CAD)</th><th>Cost code</th></tr>
                </thead>
                <tbody>
                    {visibleLines.map(line => (
                        <tr key={line.id} style={pending[line.id] !== undefined ? { background: "var(--bs-warning-bg-subtle)" } : undefined}>
                            <td>{line.vendor.name}</td>
                            <td>{line.productName}</td>
                            <td className="text-end">{formatCurrency(lineAnnualCad(line))}</td>
                            <td style={{ minWidth: 280 }}>
                                <SelectInput options={options} value={costCodeOf(line)} onChange={e => assign(line.id, e.target.value)} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    )
}

// Products → cost codes (plan), then Acumatica bills → products (actuals).
const MappingTab = (props: MappingTabProps) => {
    if (props.lines.length === 0) return <EmptyState title="No lines yet" subtitle="Add your budget lines in the Budget tab first." />

    return (
        <div className="d-flex flex-column gap-4">
            <CostCodeMapping {...props} />
            <BillsSection budget={props.budget} lines={props.lines} />
        </div>
    )
}

export default MappingTab
